import React, { useState } from "react";
import axios from "axios";
import { ArrowUpRight, Mail, Phone, MapPin, Send } from "lucide-react";
import { SITE } from "../mock";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { useToast } from "../hooks/use-toast";
import Seo from "../components/Seo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Contact = () => {
  const { toast } = useToast();
  const [form, setForm] = useState({
    name: "",
    email: "",
    company: "",
    project: "",
    position: "",
    dates: "",
    location: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.message) {
      toast({ title: "Almost there", description: "Please add at least your name and a message." });
      return;
    }
    setSubmitting(true);
    try {
      const payload = { ...form };
      if (!payload.email) delete payload.email;
      await axios.post(`${API}/contact`, payload);
      toast({
        title: "Inquiry received",
        description: "Thanks — your message was sent to Jesse. You'll hear back within 1–2 business days.",
      });
      setForm({ name: "", email: "", company: "", project: "", position: "", dates: "", location: "", message: "" });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast({
        title: "Couldn't send",
        description: detail || "Something went wrong. Please try again or email info@propmeister.art directly.",
        variant: "destructive",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-[#0E0E0F] text-[#EDE3CB]">
      <Seo
        path="/contact"
        title="Contact"
        description="Available for features, series, additional photography, commercials, music videos, prop fabrication, and archive work. Email info@propmeister.art or send the brief."
      />
      {/* Header */}
      <section className="max-w-[1440px] mx-auto px-6 lg:px-12 pt-20 pb-12 grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
        <div className="lg:col-span-8">
          <div className="section-label">Contact</div>
          <h1 className="font-display text-6xl md:text-8xl mt-4 leading-[0.9]">Props, what's your <span className="brass-text">20</span>?</h1>
        </div>
      </section>

      <div className="brass-divider max-w-[1440px] mx-auto opacity-40" />

      <section className="max-w-[1440px] mx-auto px-6 lg:px-12 py-16 grid grid-cols-1 lg:grid-cols-12 gap-12">
        {/* Info column */}
        <div className="lg:col-span-4">
          <div className="section-label">Direct</div>
          <div className="mt-6 space-y-5">
            <a href={`mailto:${SITE.email}`} className="group flex items-start gap-4">
              <Mail className="text-[#B8935A] mt-1" size={18} />
              <div>
                <div className="text-xs tracking-[0.28em] uppercase text-[#EDE3CB]/55">Email</div>
                <div className="text-lg group-hover:text-[#B8935A] transition-colors">{SITE.email}</div>
              </div>
            </a>
            <a href={`tel:${SITE.phone}`} className="group flex items-start gap-4">
              <Phone className="text-[#B8935A] mt-1" size={18} />
              <div>
                <div className="text-xs tracking-[0.28em] uppercase text-[#EDE3CB]/55">Phone</div>
                <div className="text-lg group-hover:text-[#B8935A] transition-colors">{SITE.phone}</div>
              </div>
            </a>
            <div className="flex items-start gap-4">
              <MapPin className="text-[#B8935A] mt-1" size={18} />
              <div>
                <div className="text-xs tracking-[0.28em] uppercase text-[#EDE3CB]/55">Based</div>
                <div className="text-lg">{SITE.basedIn}</div>
                <div className="text-sm text-[#EDE3CB]/65 mt-1">{SITE.travel}</div>
              </div>
            </div>
          </div>

          <div className="brass-divider mt-10 opacity-40" />

          <div className="mt-8 space-y-3">
            <div className="section-label">Affiliations</div>
            <div className="text-sm text-[#EDE3CB]/75">IATSE Local 44 · Los Angeles Based Property Master</div>
            <div className="text-sm text-[#EDE3CB]/75">Former Secretary of the Property Masters Guild</div>
            <div className="text-sm text-[#EDE3CB]/75">Member of the Academy of Television Arts & Sciences</div>
            <div className="flex gap-4 pt-3">
              <a href={SITE.imdb} target="_blank" rel="noreferrer" className="text-xs tracking-[0.22em] uppercase text-[#EDE3CB]/75 hover:text-[#B8935A] link-underline">IMDb</a>
            </div>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={onSubmit} data-testid="contact-inquiry-form" className="lg:col-span-8 border border-white/10 p-6 md:p-10 bg-[#0B0B0C]">
          <div className="section-label mb-6">Inquiry</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div>
              <Label className="text-xs tracking-[0.22em] uppercase text-[#EDE3CB]/70">Name</Label>
              <Input data-testid="contact-name-input" value={form.name} onChange={(e) => update("name", e.target.value)} placeholder="Your name" className="mt-2 bg-transparent border-white/15 focus-visible:border-[#B8935A] focus-visible:ring-0 text-[#EDE3CB] placeholder:text-[#EDE3CB]/35" />
            </div>
            <div>
              <Label className="text-xs tracking-[0.22em] uppercase text-[#EDE3CB]/70">Email</Label>
              <Input data-testid="contact-email-input" type="email" value={form.email} onChange={(e) => update("email", e.target.value)} placeholder="you@production.com" className="mt-2 bg-transparent border-white/15 focus-visible:border-[#B8935A] focus-visible:ring-0 text-[#EDE3CB] placeholder:text-[#EDE3CB]/35" />
            </div>
            <div>
              <Label className="text-xs tracking-[0.22em] uppercase text-[#EDE3CB]/70">Company</Label>
              <Input data-testid="contact-company-input" value={form.company} onChange={(e) => update("company", e.target.value)} placeholder="Production company" className="mt-2 bg-transparent border-white/15 focus-visible:border-[#B8935A] focus-visible:ring-0 text-[#EDE3CB] placeholder:text-[#EDE3CB]/35" />
            </div>
            <div>
              <Label className="text-xs tracking-[0.22em] uppercase text-[#EDE3CB]/70">Project Title</Label>
              <Input data-testid="contact-project-input" value={form.project} onChange={(e) => update("project", e.target.value)} placeholder="Project title" className="mt-2 bg-transparent border-white/15 focus-visible:border-[#B8935A] focus-visible:ring-0 text-[#EDE3CB] placeholder:text-[#EDE3CB]/35" />
            </div>
            <div>
              <Label className="text-xs tracking-[0.22em] uppercase text-[#EDE3CB]/70">Position</Label>
              <Select value={form.position} onValueChange={(v) => update("position", v)}>
                <SelectTrigger data-testid="contact-position-select" className="mt-2 bg-transparent border-white/15 text-[#EDE3CB] focus:ring-0 focus:border-[#B8935A]">
                  <SelectValue placeholder="Select position" />
                </SelectTrigger>
                <SelectContent className="bg-[#0E0E0F] border-white/15 text-[#EDE3CB]">
                  <SelectItem value="property-master">Property Master</SelectItem>
                  <SelectItem value="assistant-property-master">Assistant Property Master</SelectItem>
                  <SelectItem value="la-unit">LA Unit</SelectItem>
                  <SelectItem value="fabrication">Fabrication / Specialty</SelectItem>
                  <SelectItem value="archive">Archive / Specialty Work</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs tracking-[0.22em] uppercase text-[#EDE3CB]/70">Shoot Dates</Label>
              <Input data-testid="contact-dates-input" value={form.dates} onChange={(e) => update("dates", e.target.value)} placeholder="e.g. Mar 4 — Apr 26, 2025" className="mt-2 bg-transparent border-white/15 focus-visible:border-[#B8935A] focus-visible:ring-0 text-[#EDE3CB] placeholder:text-[#EDE3CB]/35" />
            </div>
            <div>
              <Label className="text-xs tracking-[0.22em] uppercase text-[#EDE3CB]/70">Location</Label>
              <Input data-testid="contact-location-input" value={form.location} onChange={(e) => update("location", e.target.value)} placeholder="Shoot location" className="mt-2 bg-transparent border-white/15 focus-visible:border-[#B8935A] focus-visible:ring-0 text-[#EDE3CB] placeholder:text-[#EDE3CB]/35" />
            </div>
          </div>
          <div className="mt-5">
            <Label className="text-xs tracking-[0.22em] uppercase text-[#EDE3CB]/70">Message</Label>
            <Textarea
              data-testid="contact-message-input"
              value={form.message}
              onChange={(e) => update("message", e.target.value)}
              rows={6}
              placeholder="Brief description of the project, props needs, and any specifics about scope..."
              className="mt-2 bg-transparent border-white/15 focus-visible:border-[#B8935A] focus-visible:ring-0 text-[#EDE3CB] placeholder:text-[#EDE3CB]/35"
            />
          </div>
          <div className="mt-6 flex flex-wrap items-center justify-between gap-4">
            <p className="text-xs text-[#EDE3CB]/55">All inquiries reviewed personally. Replies typically within 1–2 business days.</p>
            <button data-testid="contact-submit-btn" type="submit" disabled={submitting} className="btn-primary">
              {submitting ? "Sending..." : (<>Send Inquiry <Send size={14} /></>)}
            </button>
          </div>
        </form>
      </section>

      {/* Bottom strip */}
      <section className="border-t border-white/5">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12 py-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <div className="font-display text-2xl tracking-wide">Available worldwide · Primary credits LA / ATL / NYC / NOLA / CHI</div>
          <a href={`mailto:${SITE.email}`} className="text-[#B8935A] tracking-[0.22em] uppercase text-xs link-underline inline-flex items-center gap-2">
            Email Direct <ArrowUpRight size={14} />
          </a>
        </div>
      </section>
    </div>
  );
};

export default Contact;
